import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Sparkles, Users, Send, Calendar, MapPin, Gift, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';

const features = [
  {
    icon: Sparkles,
    title: "Diseños Elegantes",
    description: "3 plantillas premium personalizables con colores, tipografías e imágenes"
  },
  {
    icon: Users,
    title: "Gestión de Invitados",
    description: "Organiza familias y grupos, importa desde CSV y controla todo desde un panel"
  },
  {
    icon: Send,
    title: "Envío por WhatsApp",
    description: "Genera enlaces personalizados para enviar invitaciones por WhatsApp"
  },
  {
    icon: Calendar,
    title: "RSVP Inteligente",
    description: "Confirmaciones familiares con un solo enlace, sin complicaciones"
  }
];

const templates = [
  {
    name: "Elegante Clásico",
    image: "https://images.unsplash.com/photo-1761211488173-a7154314420a?w=600&q=80",
    description: "Sofisticación atemporal"
  },
  {
    name: "Moderno Minimalista",
    image: "https://images.unsplash.com/photo-1679097316266-e760624ee5e0?w=600&q=80",
    description: "Líneas limpias y elegantes"
  },
  {
    name: "Romántico Natural",
    image: "https://images.unsplash.com/photo-1758810410268-5e9d85b60065?w=600&q=80",
    description: "Calidez y naturaleza"
  }
];

export default function LandingPage() {
  const [hoveredTemplate, setHoveredTemplate] = useState(null);

  return (
    <div className="min-h-screen bg-wedding-background">
      {/* Navigation */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 bg-white/70 backdrop-blur-xl border border-white/20 rounded-full px-8 py-4 shadow-sm z-50 flex items-center gap-8">
        <Link to="/" className="font-heading text-xl text-wedding-text">
          Wedvites
        </Link>
        <div className="hidden md:flex items-center gap-6">
          <a href="#features" className="text-sm text-wedding-text-muted hover:text-wedding-primary transition-colors">
            Características
          </a>
          <a href="#templates" className="text-sm text-wedding-text-muted hover:text-wedding-primary transition-colors">
            Plantillas
          </a>
          <a href="#pricing" className="text-sm text-wedding-text-muted hover:text-wedding-primary transition-colors">
            Precios
          </a>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" className="text-wedding-primary hover:bg-wedding-accent" data-testid="login-btn">
              Iniciar Sesión
            </Button>
          </Link>
          <Link to="/register">
            <Button className="bg-wedding-primary hover:bg-wedding-primary-dark text-white rounded-full px-6" data-testid="register-btn">
              Comenzar
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1768777272833-358b38cc41cc?w=1920&q=80"
            alt="Wedding background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-wedding-background"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white/80 uppercase tracking-[0.3em] text-sm mb-6"
          >
            Invitaciones Digitales de Boda
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl text-white mb-6 tracking-tight"
          >
            Crea Momentos
            <br />
            <span className="font-accent text-6xl md:text-8xl lg:text-9xl">Inolvidables</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto font-body"
          >
            Diseña invitaciones elegantes, gestiona tus invitados y recibe confirmaciones en tiempo real
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/register">
              <Button
                size="lg"
                className="bg-white text-wedding-primary hover:bg-wedding-accent rounded-full px-10 py-6 text-sm uppercase tracking-widest font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="hero-cta-btn"
              >
                Crear Mi Invitación
              </Button>
            </Link>
            <a href="#templates">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/20 rounded-full px-10 py-6 text-sm uppercase tracking-widest font-semibold"
                data-testid="view-templates-btn"
              >
                Ver Plantillas
              </Button>
            </a>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 bg-white/80 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-wedding-primary uppercase tracking-[0.2em] text-sm mb-4">
              Todo lo que necesitas
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-wedding-text tracking-tight">
              Características
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/80 backdrop-blur-md border border-white/20 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-wedding-accent rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-wedding-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl text-wedding-text mb-3">
                  {feature.title}
                </h3>
                <p className="text-wedding-text-muted font-body text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section id="templates" className="py-24 md:py-32 px-6 bg-wedding-accent/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-wedding-primary uppercase tracking-[0.2em] text-sm mb-4">
              Diseños Premium
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-wedding-text tracking-tight">
              Plantillas
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {templates.map((template, index) => (
              <motion.div
                key={template.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group cursor-pointer"
                onMouseEnter={() => setHoveredTemplate(index)}
                onMouseLeave={() => setHoveredTemplate(null)}
                data-testid={`template-card-${index}`}
              >
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-6">
                  <img
                    src={template.image}
                    alt={template.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-wedding-primary/60 flex items-center justify-center transition-opacity duration-300 ${hoveredTemplate === index ? 'opacity-100' : 'opacity-0'}`}>
                    <Link to="/register">
                      <Button className="bg-white text-wedding-primary hover:bg-wedding-accent rounded-full px-8 py-4 text-sm uppercase tracking-widest">
                        Usar Plantilla
                      </Button>
                    </Link>
                  </div>
                </div>
                <h3 className="font-heading text-2xl text-wedding-text text-center">
                  {template.name}
                </h3>
                <p className="text-wedding-text-muted text-center text-sm mt-2">
                  {template.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 md:py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-wedding-primary uppercase tracking-[0.2em] text-sm mb-4">
              Planes Flexibles
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-wedding-text tracking-tight">
              Precios Transparentes
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                id: "basic",
                name: "Básico",
                price: "0",
                description: "Perfecto para celebraciones íntimas",
                features: ["50 Invitados", "Plantilla Estándar", "RSVP Básico", "Mapa del Evento"],
                cta: "Comenzar Gratis",
                popular: false
              },
              {
                id: "premium",
                name: "Premium",
                price: "49.99",
                description: "La experiencia completa para tu boda",
                features: ["150 Invitados", "3 Plantillas Premium", "Galería de Fotos", "Música de Fondo", "Lista de Regalos"],
                cta: "Elegir Premium",
                popular: true
              },
              {
                id: "pro",
                name: "Pro / Agencia",
                price: "99.99",
                description: "Para bodas masivas o wedding planners",
                features: ["Invitados Ilimitados", "Múltiples Eventos", "Soporte Prioritario", "Sin Marca de Agua", "WhatsApp Masivo"],
                cta: "Contratar Pro",
                popular: false
              }
            ].map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-8 rounded-3xl border ${plan.popular ? 'border-wedding-primary bg-wedding-accent/20' : 'border-slate-200 bg-white'} shadow-sm flex flex-col`}
              >
                {plan.popular && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-wedding-primary text-white px-4 py-1 rounded-full text-xs font-bold font-heading">
                    MÁS POPULAR
                  </span>
                )}
                <div className="mb-8">
                  <h3 className="font-heading text-2xl mb-2">{plan.name}</h3>
                  <p className="text-wedding-text-muted text-sm">{plan.description}</p>
                </div>
                <div className="mb-8 flex items-baseline gap-1">
                  <span className="text-4xl font-bold font-heading">${plan.price}</span>
                  <span className="text-slate-500 text-sm">/ evento</span>
                </div>
                <ul className="space-y-4 mb-10 flex-1">
                  {plan.features.map(feature => (
                    <li key={feature} className="flex items-start text-sm text-slate-600">
                      <Heart className="w-4 h-4 text-wedding-primary mr-3 mt-0.5" fill="currentColor" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to={plan.price === "0" ? "/register" : "/pricing"}>
                  <Button className={`w-full rounded-xl py-6 ${plan.popular ? 'bg-wedding-primary' : 'bg-slate-900'} text-white`}>
                    {plan.cta}
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-wedding-primary uppercase tracking-[0.2em] text-sm mb-4">
              Simple y Elegante
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-wedding-text tracking-tight">
              Cómo Funciona
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { step: "01", title: "Diseña", description: "Elige una plantilla y personalízala con tus colores, fotos y textos", icon: Sparkles },
              { step: "02", title: "Invita", description: "Importa tu lista de invitados y genera enlaces únicos para cada familia", icon: Users },
              { step: "03", title: "Confirma", description: "Recibe las confirmaciones y gestiona todo desde tu panel", icon: Heart }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="text-center"
                data-testid={`how-it-works-${index}`}
              >
                <div className="relative inline-block mb-6">
                  <span className="font-heading text-8xl text-wedding-accent/50">
                    {item.step}
                  </span>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-wedding-primary rounded-full flex items-center justify-center">
                    <item.icon className="w-8 h-8 text-white" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="font-heading text-2xl text-wedding-text mb-3">
                  {item.title}
                </h3>
                <p className="text-wedding-text-muted font-body max-w-xs mx-auto">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 bg-wedding-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              Comienza Tu Historia
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto font-body">
              Crea tu invitación digital en minutos y sorprende a tus invitados con una experiencia única
            </p>
            <Link to="/register">
              <Button
                size="lg"
                className="bg-white text-wedding-primary hover:bg-wedding-accent rounded-full px-12 py-6 text-sm uppercase tracking-widest font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                data-testid="cta-final-btn"
              >
                Crear Invitación Gratis
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-wedding-background border-t border-wedding-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-wedding-primary" fill="currentColor" />
            <span className="font-heading text-xl text-wedding-text">Wedvites</span>
          </div>
          <p className="text-wedding-text-muted text-sm">
            © 2026 Wedvites. Hecho con amor para tu día especial.
          </p>
        </div>
      </footer>
    </div>
  );
}
